// Partner ad-referral attribution (Trade Into Cash).
//
// Trade Into Cash sends traffic to bucketlistguitars.com/sell/?tic_click=XXXXX.
// We do two things with that click id:
//
//   1. Stamp `Ad Source = "tradeintocash.com"` on the Airtable Orders record so
//      Dan can see, in the tool he already lives in, which POs came from the
//      partnership. The raw click id rides along into our own database so a
//      commission invoice can be reconciled order-for-order if it's ever
//      disputed.
//   2. Fire their conversion pixel on the confirmation screen, which is what
//      they bill the 5% against.
//
// The storage key and pixel URL below match the snippet Trade Into Cash sent us
// verbatim. If Dan also pastes their site-wide header script into WordPress,
// the two write the same key and simply agree — no double-counting, no
// conflict. Capturing here as well means attribution still works if that header
// script is never installed, since their ads land directly on /sell/.
//
// NOTE ON STORAGE: sessionStorage, not localStorage, and deliberately so. The
// sell app calls localStorage.clear() when a stale cart expires (see App.js),
// which would silently wipe a click id parked there. sessionStorage is
// untouched by that. The tradeoff is a narrower attribution window — the id
// dies with the browser tab, so a seller who returns tomorrow in a fresh window
// is not counted. That undercounts in Trade Into Cash's disfavor, never ours.

const CLICK_PARAM = 'tic_click';
const STORAGE_KEY = 'tic_click';
const AD_SOURCE = 'tradeintocash.com';
const PIXEL_URL = 'https://tradeintocash.com/.netlify/functions/conversion-pixel';
const PARTNER = 'bucketlist';

// Read the click id out of the landing URL and park it for the rest of the
// session. Safe to call on every load: absent param means absent id, and we
// never overwrite a stored id with nothing.
//
// Every function in this module is wrapped in try/catch and returns a benign
// value on failure. Storage access throws outright in Safari private mode and
// under some embedded browsers, and attribution must never be able to break a
// seller's checkout.
export function captureAdClick() {
  try {
    const clickId = new URLSearchParams(window.location.search).get(CLICK_PARAM);
    if (clickId) {
      window.sessionStorage.setItem(STORAGE_KEY, clickId);
    }
  } catch (err) {
    // No-op. See note above.
  }
}

// Attribution fields for the quote mutation. Returns an empty object when this
// visit has no partner click id, so the GraphQL variables stay undefined and
// the backend omits the Airtable field entirely rather than writing a blank.
export function getAdAttribution() {
  try {
    const clickId = window.sessionStorage.getItem(STORAGE_KEY);
    if (!clickId) return {};
    return { adSource: AD_SOURCE, adClickId: clickId };
  } catch (err) {
    return {};
  }
}

// Fire Trade Into Cash's 1x1 conversion pixel, then drop the click id so a
// refresh of the confirmation screen can't report the same order twice.
//
// `order` carries the detail they asked for in their second spec:
//   orderId  -> our PO number, so an invoice line can be matched to a real order
//   device   -> the item name(s) on the trade-in
//   amount   -> the QUOTED total at checkout
//
// IMPORTANT about amount: this fires when the seller submits, which is before
// the gear ships, before inspection, and before we pay. Their 5% is owed on the
// final paid total, so this number is an opening figure that adjustments and
// non-arrivals will move DOWN. It is not an invoice.
//
// An order with several items sends all their names joined, not just the first.
// Their parameter is singular ("device") but our carts are not.
//
// referrerPolicy is set to no-referrer on purpose: without it the browser
// attaches our confirmation-page URL to the request, which leaks more about a
// customer's session to a third party than the partnership needs.
export function firePartnerConversionPixel(order = {}) {
  try {
    const clickId = window.sessionStorage.getItem(STORAGE_KEY);
    if (!clickId) return;

    const param = (value) => encodeURIComponent(value == null ? '' : String(value));

    const img = new Image();
    img.referrerPolicy = 'no-referrer';
    img.src =
      `${PIXEL_URL}?click_id=${encodeURIComponent(clickId)}` +
      `&partner=${PARTNER}` +
      `&order_id=${param(order.orderId)}` +
      `&device=${param(order.device)}` +
      `&amount=${param(order.amount)}` +
      `&t=${Date.now()}`;

    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch (err) {
    // No-op. A missed pixel costs us nothing; a thrown error costs a sale.
  }
}
